import { Injectable, inject } from "@angular/core";
import { MessageService } from "primeng/api";
import { LoggingService, LogLevel } from "./logging.service";

export interface Publisher {
  topic: string;
  name: string;
}

export interface BackendMessage {
  level: string;
  title: string;
  content: string;
}

export interface ServiceMessage {
  level: string;
  title: string;
  content: string;
}

@Injectable()
export class NotificationService {
  private messageService = inject(MessageService);
  private loggingService = inject(LoggingService);

  public publish(
    topic: string,
    level: string,
    title: string,
    content: any
  ): void {
    this.loggingService.log(
      "NotificationService",
      "Received message for publication via publish! topic=" +
        topic +
        "; level=" +
        level +
        "; title=" +
        title +
        "; content=" +
        JSON.stringify(content),
      LogLevel.DEBUG
    );
    this.messageService.add({
      key: topic,
      severity: level,
      summary: title,
      detail: content
    });
  }

  public publishMessage(topic: string, message: ServiceMessage): void {
    this.loggingService.log(
      "NotificationService",
      "Received message for publication via publishMessage! topic=" +
        topic +
        "; message=" +
        JSON.stringify(message),
      LogLevel.DEBUG
    );
    this.messageService.add({
      key: topic,
      severity: message.level,
      summary: message.title,
      detail: message.content
    });
  }

  /**
   * Show a dismissible notification with optional callbacks
   * @param key Unique key for the notification
   * @param level Severity level (error, warn, info, success)
   * @param title Notification title
   * @param content Notification message content
   * @param dismissCallback Optional callback when notification is dismissed
   * @param actionCallback Optional callback when action button is clicked
   */
  public showDismissible(
    key: string,
    level: string,
    title: string,
    content: string,
    dismissCallback?: () => void,
    actionCallback?: () => void
  ): void {
    this.loggingService.log(
      "NotificationService",
      "Showing dismissible notification! key=" +
        key +
        "; level=" +
        level +
        "; title=" +
        title,
      LogLevel.DEBUG
    );
    
    // Create a unique key with timestamp to ensure we can clear specific notifications
    const uniqueKey = `${key}-${Date.now()}`;
    
    this.messageService.add({
      key: uniqueKey,
      severity: level,
      summary: title,
      detail: content,
      sticky: true, // Keep until dismissed
      closable: true, // Show close button
      life: 0 // Don't auto-dismiss
    });

    // Store callbacks for later execution
    if (dismissCallback) {
      // Store dismiss callback - we'll need to track this
      this.loggingService.log(
        "NotificationService",
        "Dismiss callback registered for key: " + uniqueKey,
        LogLevel.DEBUG
      );
    }

    if (actionCallback) {
      this.loggingService.log(
        "NotificationService",
        "Action callback registered for key: " + uniqueKey,
        LogLevel.DEBUG
      );
    }
  }

  /**
   * Clear notification by key
   * @param key The key to clear
   */
  public clearByKey(key: string): void {
    this.loggingService.log(
      "NotificationService",
      "Clearing notification by key: " + key,
      LogLevel.DEBUG
    );
    this.messageService.clear(key);
  }

  /**
   * Clear notifications by topic (prefix match)
   * @param topic The topic/prefix to clear
   */
  public clearByTopic(topic: string): void {
    this.loggingService.log(
      "NotificationService",
      "Clearing notifications by topic: " + topic,
      LogLevel.DEBUG
    );
    // PrimeNG doesn't support clear by prefix, so we need to track keys
    // For now, clear all notifications with the topic prefix
    this.messageService.clear(topic);
  }
}
